import { PayloadAction, createSlice } from '@reduxjs/toolkit';

import { UserCompany, userApi } from '@/store/api';

import { CompanyRole, Side } from '@/utils/enums';

import type { RootState } from '../store';

export type SelectedCompany = UserCompany | null;

export type UserState = {
  selectedCompany: SelectedCompany;
  selectedSide: Side | '';
};

const loadSelectedCompanyId = (): string => {
  try {
    const saved = localStorage.getItem('user.selectedCompanyId');
    return saved ? JSON.parse(saved) : '';
  } catch {
    return '';
  }
};

const initialState: UserState = {
  selectedCompany: null,
  selectedSide: Side.BUYER,
};

const _calculateCompanySide = (company: UserCompany) => {
  let newSide: Side;

  switch (company.companyRole) {
    case CompanyRole.BUYER_ONLY:
      newSide = Side.BUYER;
      break;
    case CompanyRole.SELLER_ONLY:
      newSide = Side.SELLER;
      break;
    default:
      newSide = Side.BUYER;
  }

  return newSide;
};

const _setNewSelectedCompany = (state: UserState, newCompany: UserCompany) => {
  localStorage.setItem('user.selectedCompanyId', JSON.stringify(newCompany.id));

  const newSide = _calculateCompanySide(newCompany);

  state.selectedSide = newSide;
  state.selectedCompany = newCompany;
};

/**
 * Applies a `/users/me` response that still contains the company already selected.
 *
 * The company object is refreshed, but the side is kept: the query refetches whenever its tag is
 * invalidated or the API cache is reset, and re-deriving the side on each of those sent a
 * bi-directional company back to the buyer side the user had just left. Only `BI_DIRECTIONAL` keeps
 * its side, the same test the side selector uses: every other role has no side to choose, so a
 * company whose role has changed since the last fetch cannot keep a side it no longer offers.
 */
const _refreshSelectedCompany = (state: UserState, company: UserCompany) => {
  const canChooseSide = company.companyRole === CompanyRole.BI_DIRECTIONAL;

  if (!canChooseSide || !state.selectedSide) {
    state.selectedSide = _calculateCompanySide(company);
  }

  state.selectedCompany = company;
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setSelectedCompany: (state, action: PayloadAction<UserCompany>) => {
      _setNewSelectedCompany(state, action.payload);
    },
    setSelectedSide: (state, action: PayloadAction<Side>) => {
      state.selectedSide = action.payload;
    },
  },
  extraReducers: builder => {
    builder.addMatcher(
      userApi.endpoints.getCurrentUser.matchFulfilled,
      (state, action) => {
        if (action.payload.companies.length) {
          const storedSelectedCompanyId = loadSelectedCompanyId();
          const newCompany =
            action.payload.companies.find(
              company => company.id === storedSelectedCompanyId
            ) || action.payload.companies[0];

          if (newCompany.id === state.selectedCompany?.id) {
            _refreshSelectedCompany(state, newCompany);
          } else {
            _setNewSelectedCompany(state, newCompany);
          }
        }
      }
    );
  },
});

// actions
export const { setSelectedCompany, setSelectedSide } = userSlice.actions;

// selectors
const selectCurrentUser = (state: RootState) =>
  userApi.endpoints.getCurrentUser.select()(state)?.data;

export const selectIsSuperUser = (state: RootState) => {
  const user = selectCurrentUser(state);
  return user?.isPlatformAdmin || user?.isTechnicalSupport || false;
};

export const selectIsPlatformAdmin = (state: RootState) => {
  const user = selectCurrentUser(state);
  return user?.isPlatformAdmin || false;
};

export const selectIsTechnicalSupport = (state: RootState) => {
  const user = selectCurrentUser(state);
  return user?.isTechnicalSupport || false;
};

export const selectIsCompanyAdmin = (state: RootState) => {
  const user = selectCurrentUser(state);
  return user?.companies.some(company => !!company.isCompanyAdmin) || false;
};

export const selectIsCompanyAdminOfCompany =
  (companyId: string | null) => (state: RootState) => {
    const user = selectCurrentUser(state);
    return !!user?.companies.find(company => company.id === companyId)
      ?.isCompanyAdmin;
  };

export const selectIsAdvisorAdminOfSelectedCompany = (state: RootState) => {
  const selectedCompany = state.user.selectedCompany;
  return (
    !!selectedCompany &&
    selectedCompany.companyRole === CompanyRole.ADVISOR &&
    selectIsCompanyAdminOfCompany(selectedCompany.id)(state)
  );
};

export default userSlice.reducer;
